import { observable, computed, action } from 'mobx';
import { api, socket, authService } from '../../services/index';
import { getEmote } from '../helpers/cdn';
import TableItemNotEmotes from './TableItemNotEmotes';
import TableRows from './TableRows';
import throttle from 'lodash/throttle';
import { track } from '@streamelements/alchemy';

class StatsResponse {
    @observable channel = 'global';
    @observable totalMessages = 0;
    @observable prevTotalMessagesCounter = 0;
    @observable chatters = new TableRows();
    @observable hashtags = new TableRows();
    @observable commands = new TableRows();
    @observable bttvEmotes = new TableRows();
    @observable ffzEmotes = new TableRows();
    @observable sevenTVEmotes = new TableRows();
    @observable twitchEmotes = new TableRows();
    @observable messagesPerSecond = 0;
    @observable is404 = false;
    @observable sponsorships = [];
    @observable user = null;
    @observable track = track;
    @observable userLoading = true;
    @observable sponsorshipsLoading = true;

    @observable emotesBar = [];
    // @observable messageCounter = 0; // for message per second.

    constructor () {
        this.measure();
        this.cachedEmotesBar = [];
        this.messageCounter = 0;
        this.firstRequest = true;
        this.updateEvent = this.updateEvent.bind(this);
        this.updateEmotesBarState = throttle(this.updateEmotesBarState.bind(this), 100);
        this.lastEmoteId = null;
        this.track = track;
        this.userLoading = true;
        this.sponsorshipsLoading = true;

        authService.getUser().then(data => {
            this.user = data;
            this.userLoading = false;
        }).catch(err => {
            this.userLoading = false;
        })
        
        authService.getSponsorships().then(sponsorships => {
            this.sponsorships = sponsorships;
            this.sponsorshipsLoading = false;
        })
        .catch(e => {
            this.sponsorshipsLoading = false;
            this.sponsorships = [];
        });    
    }

    @computed get numOfTwitchEmotes () {
        return this.twitchEmotes.length;
    }

    @computed get totalMessagesCounter () {
        return this.totalMessages;
    }

    @action
    fetchStats (channel = 'global') {
        if (!this.firstRequest) {
            socket.unsubscribeFromChannel(this.channel);
        } else {
            this.firstRequest = false;
        }
        const updateFunc = this.updateEvent.bind(this);
        if (this.is404) {
            this.is404 = false;
        }
        api.getStats(channel, {limit: 100})
            .then(res => {
                const {data} = res;

                this.generateTableRows(null, data);
                this.totalMessages = data.totalMessages;
            })
            .then(() => socket.connect())
            .then(() => {
                socket.whatToDoOnUpdate(updateFunc);
                socket.updateChannel(channel);
            })
            .then(() => console.log('Connected'))
            .catch(e => {
                if (e.response && e.response.status === 404) {
                    this.is404 = true;
                    return;
                }
                console.log('e', e);
                this.generateTableRows('error', e);
            });
    }

    @action updateTotalMessages (totalMessages) {
        this.totalMessages = totalMessages;
    }

    @action updateEmotesBar (provider, {id}, messageNumber) {

        let emotesBar = this.cachedEmotesBar;
        const maxLength = 15;
        const key = `${id}:${messageNumber}`;

        emotesBar.unshift({ emote: getEmote(provider, id, 1), key });

        if (emotesBar.length > maxLength + 1) {
            emotesBar.pop();
        }

        this.cachedEmotesBar = emotesBar;

        this.updateEmotesBarState(emotesBar);
    }

    @action updateEmotesBarState(emotesBar) {
        this.emotesBar = emotesBar;
    }

    @action updateEvent (emotes) {

        // event - {type, key, amount, provider [opt]}
        this.messageCounter++;
        this.prevTotalMessagesCounter = this.totalMessages;
        this.totalMessages++;

        emotes.forEach((event) => this.emoteUpdateEvent(event));
    }

    @action emoteUpdateEvent(event) {
        const nonEmotesTypes = {
            chatters: true,
            hashtags: true,
            commands: true,
        };

        let { provider, type, key, amount } = event;

        // Hack because we can't have a key with '7'
        if (provider === '7tv') {
            event.provider = 'sevenTV';
            provider = 'sevenTV';
        }

        if (type === 'emotes') {
            const emotes = this[`${provider}Emotes`];
            const emoteId = event.id;
            emotes.updateTableRow(event);
            if (!this.lastEmoteId || emoteId !== this.lastEmoteId) {
                this.updateEmotesBar(provider, event, this.totalMessages);
                this.lastEmoteId = event.id;
            }
        } else if (nonEmotesTypes[type]) {
            const tableItems = this[type];

            tableItems.updateTableRow(event);
        } else {
            return;
        }
    }

    @action measure () {
        this.mpsIntervalId = window.setInterval(this.measureFunc.bind(this), 1000);
    }

    measureFunc() {
        const counter = this.messageCounter;
        this.messageCounter = 0;
        this.messagesPerSecond = counter;
    }

    @computed get _messagesPerSecond () {
        return this.messagesPerSecond;
    }

    onChangeChannel (channel) {
        this.channel = channel;
        this.totalMessages = 0;
        this.chatters = new TableRows();
        this.hashtags = new TableRows();
        this.commands = new TableRows();
        this.bttvEmotes = new TableRows();
        this.ffzEmotes = new TableRows();
        this.sevenTVEmotes = new TableRows();
        this.twitchEmotes = new TableRows();
        this.messagesPerSecond = 0;
        this.emotesBar = [];
        this.lastEmoteId = null;
        this.messageCounter = 0;

        window.clearInterval(this.mpsIntervalId);
        this.measure();

        this.fetchStats(channel);
        
        track({
          "feature": 'public_pages',
          "name": "chatstats_page_load",
          "type": "page_load",
          "product": "dashboard",
          "fields": [
            ['stats_page', channel]
          ],
        });
    }

    generateTableRows (err, data) {
        const tableTypes = [
            {
                key: 'twitchEmotes',
                rowType: 'emotes',
                keyValue: 'emote',
            },
            {
                key: 'bttvEmotes',
                rowType: 'emotes',
                keyValue: 'emote',
            },
            {
                key: 'ffzEmotes',
                rowType: 'emotes',
                keyValue: 'emote',
            },
            {
                key: 'sevenTVEmotes',
                rowType: 'emotes',
                keyValue: 'emote',
            },
            {
                key: 'chatters',
                rowType: 'chatters',
                keyValue: 'name',
            },
            {
                key: 'hashtags',
                rowType: 'hashtags',
                keyValue: 'hashtag',
            },
            {
                key: 'commands',
                rowType: 'commands',
                keyValue: 'command',
            },
        ];

        if (err) {
            tableTypes.forEach(t => this[t.key] = new TableRows('error'));
            return;
        }

        tableTypes.forEach((t) => this[t.key] = new TableRows(data[t.key], t.rowType, t.keyValue));

    }

}

export default StatsResponse;



// WEBPACK FOOTER //
// ./src/app/models/StatsResponse.js